import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchClients, fetchClientConfig, toggleClientStatus, createClient, updateClient } from "../../../redux/slice/brokerClientsSlice";
import { useLoader } from "../../../context/LoaderContext";
import { useToast } from "../../../context/TostContext";
import ListSection from "./ListSection";
import AddEditClientModal from "./ClientModal";
import { usePermission } from "../../../hooks/usePermission";

const MODULE_SLUG = "properties"; // Or "broker_clients" depending on the actual permission mapping

const BrokerClients = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        <span className="text-muted fw-normal">Broker CRM / </span>Clients
      </h4>
    </div>
  );
  const toast = useToast();

  const { clients = [], config = {}, totalCount = 0, isLoading, isConfigLoading } = useSelector((state) => state.brokerClients || {});

  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState({ value: 10, label: 10 });

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(MODULE_SLUG, "create");
  const canUpdate = hasPermission(MODULE_SLUG, "update");

  const fetchData = useCallback(() => {
    dispatch(fetchClients({
      search: searchTerm,
      page: currentPage,
      limit: limit.value
    }));
  }, [dispatch, searchTerm, currentPage, limit.value]);

  useEffect(() => {
    dispatch(fetchClientConfig());
  }, [dispatch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchData]);

  const handleAddClient = () => {
    setSelectedClient(null);
    setIsEditMode(false);
    setOpenModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setIsEditMode(true);
    setOpenModal(true);
  };

  const handleToggleStatus = (id) => {
    setLoading(true);
    dispatch(toggleClientStatus(id))
      .then((res) => {
        if (res.error) {
          toast.error(res.payload?.message || "Failed to update status");
        } else {
          toast.success("Client status updated successfully");
        }
      })
      .finally(() => setLoading(false));
  };

  const onSubmitForm = async (data) => {
    setIsSubmitting(true);
    try {
      const action = isEditMode
        ? updateClient({ id: selectedClient.id, data })
        : createClient(data);

      const res = await dispatch(action);

      if (res.error) {
        toast.error(res.payload?.message || `Failed to ${isEditMode ? "update" : "create"} client`);
      } else {
        toast.success(`Client ${isEditMode ? "updated" : "created"} successfully`);
        setOpenModal(false);
        fetchData(); // Refresh list
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  const paginationConfig = useMemo(() => ({
    currentPage,
    totalPages: Math.ceil(totalCount / limit?.value) || 1,
    total: totalCount || 0,
    onPageChange: (page) => setCurrentPage(page),
    pageSize: limit?.value,
    setPageSize: (size) => setLimit({ value: size, label: size }),
    setCurrentPage
  }), [currentPage, totalCount, limit]);

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
      <Container fluid className="px-0">
      <div className="bg-white rounded-2 overflow-hidden common-shadow">
        <ListSection
          data={clients}
          isLoading={isLoading || isConfigLoading}
          searchTerm={searchTerm}
          setSearchTerm={(val) => { setSearchTerm(val); setCurrentPage(1); }}
          onAdd={canCreate ? handleAddClient : null}
          onEdit={canUpdate ? handleEditClient : null}
          onDelete={canUpdate ? handleToggleStatus : null} // Using toggle status for the activate/deactivate action
          pagination={paginationConfig}
        />
      </div>

      <AddEditClientModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={isEditMode ? "Edit Client" : "Add Client"}
        size="lg"
        className="tp-file-modal"
        clientData={selectedClient}
        isEditMode={isEditMode}
        onSubmit={onSubmitForm}
        configData={config}
        isSubmitting={isSubmitting}
      />
    </Container>
  </>
  );
};

export default BrokerClients;
